import { useMemo } from "react";

// Deterministic procedural sigil generator.
// Heraldic vocabulary: outer ring + cardinal ticks + inner ring(s) + wedge(s) + central glyph.
// Seeded from a string (PDA stand-in). Same seed → same sigil.

function hashString(str: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
}

function makeRng(seed: string): () => number {
  let s = hashString(String(seed)) || 1;
  return () => {
    s ^= s << 13;
    s >>>= 0;
    s ^= s >>> 17;
    s ^= s << 5;
    s >>>= 0;
    return (s >>> 0) / 4294967296;
  };
}

export type SigilTone = "bone" | "bronze" | "oxblood" | "slate" | "auto";

export interface SigilProps {
  seed?: string;
  size?: number;
  tone?: SigilTone;
  cracked?: boolean;
  glow?: boolean;
  strokeWidth?: number;
  title?: string;
  sweeping?: boolean;
  flash?: boolean;
  /**
   * Progressive assembly. 0 = empty, 1 = outer ring + ticks, 2 = + mid ring + wedges,
   * 3 = + inner ring + square, 4 = + central glyph (fully assembled). Default 4.
   */
  layer?: 0 | 1 | 2 | 3 | 4;
}

export function Sigil({
  seed = "altheia",
  size = 120,
  tone = "auto",
  cracked = false,
  glow = false,
  strokeWidth = 1,
  title,
  sweeping = false,
  flash = false,
  layer = 4,
}: SigilProps) {
  const params = useMemo(() => {
    const rng = makeRng(seed);
    const rOuter = 46;
    const rMid = 34 + Math.floor(rng() * 4);
    const rInner = 18 + Math.floor(rng() * 6);
    const tickCount = [8, 12, 16, 24][Math.floor(rng() * 4)] ?? 12;
    const wedgeCount = 1 + Math.floor(rng() * 3);
    const wedgeSpan = 18 + Math.floor(rng() * 30);
    const wedgeStart = Math.floor(rng() * 360);
    const innerGlyphKind = Math.floor(rng() * 6);
    const innerRot = Math.floor(rng() * 360);
    const dotRingCount = 6 + Math.floor(rng() * 8);
    const hasMidTicks = rng() > 0.4;
    const hasInnerSquare = rng() > 0.5;
    const hasOuterDots = rng() > 0.3;
    const midTickCount = hasMidTicks ? 6 + Math.floor(rng() * 6) : 0;
    const crackStart = cracked ? Math.floor(rng() * 360) : 0;
    // pre-roll jitter for crack
    const crackJitter: number[] = [];
    if (cracked) {
      for (let i = 1; i < 7; i++) crackJitter.push(4 * (rng() - 0.5));
    }
    return {
      rOuter,
      rMid,
      rInner,
      tickCount,
      wedgeCount,
      wedgeSpan,
      wedgeStart,
      innerGlyphKind,
      innerRot,
      dotRingCount,
      hasMidTicks,
      hasInnerSquare,
      hasOuterDots,
      midTickCount,
      crackStart,
      crackJitter,
    };
  }, [seed, cracked]);

  const colorMap: Record<Exclude<SigilTone, "auto">, string> = {
    bone: "var(--bone)",
    bronze: "var(--bronze)",
    oxblood: "var(--oxblood)",
    slate: "var(--slate)",
  };
  const color = tone === "auto" ? colorMap.bone : colorMap[tone];
  const cx = 50;
  const cy = 50;
  const {
    rOuter,
    rMid,
    rInner,
    tickCount,
    wedgeCount,
    wedgeSpan,
    wedgeStart,
    innerGlyphKind,
    innerRot,
    dotRingCount,
    hasMidTicks,
    hasInnerSquare,
    hasOuterDots,
    midTickCount,
    crackStart,
    crackJitter,
  } = params;

  const ticks = [];
  for (let i = 0; i < tickCount; i++) {
    const ang = (i / tickCount) * Math.PI * 2 - Math.PI / 2;
    const isCardinal = i % (tickCount / 4) === 0;
    const len = isCardinal ? 6 : 3;
    const r1 = rOuter;
    const r2 = rOuter - len;
    ticks.push(
      <line
        key={`t${i}`}
        x1={cx + Math.cos(ang) * r1}
        y1={cy + Math.sin(ang) * r1}
        x2={cx + Math.cos(ang) * r2}
        y2={cy + Math.sin(ang) * r2}
        stroke={color}
        strokeWidth={isCardinal ? 0.8 * strokeWidth : 0.4 * strokeWidth}
        strokeLinecap="butt"
      />,
    );
  }

  const wedges = [];
  for (let i = 0; i < wedgeCount; i++) {
    const start = (wedgeStart + i * (360 / wedgeCount)) % 360;
    const end = start + wedgeSpan;
    const a1 = ((start - 90) * Math.PI) / 180;
    const a2 = ((end - 90) * Math.PI) / 180;
    const r = rMid - 1;
    const x1 = cx + Math.cos(a1) * r;
    const y1 = cy + Math.sin(a1) * r;
    const x2 = cx + Math.cos(a2) * r;
    const y2 = cy + Math.sin(a2) * r;
    const largeArc = end - start > 180 ? 1 : 0;
    wedges.push(
      <path
        key={`w${i}`}
        d={`M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`}
        fill={color}
        opacity="0.12"
      />,
    );
  }

  const outerDots = [];
  if (hasOuterDots) {
    for (let i = 0; i < dotRingCount; i++) {
      const ang = (i / dotRingCount) * Math.PI * 2;
      outerDots.push(
        <circle
          key={`od${i}`}
          cx={cx + Math.cos(ang) * 26}
          cy={cy + Math.sin(ang) * 26}
          r={0.7}
          fill={color}
        />,
      );
    }
  }

  const midTicks = [];
  if (hasMidTicks) {
    for (let i = 0; i < midTickCount; i++) {
      const ang = (i / midTickCount) * Math.PI * 2 + Math.PI / midTickCount;
      const r1 = rMid;
      const r2 = rMid + 3;
      midTicks.push(
        <line
          key={`mt${i}`}
          x1={cx + Math.cos(ang) * r1}
          y1={cy + Math.sin(ang) * r1}
          x2={cx + Math.cos(ang) * r2}
          y2={cy + Math.sin(ang) * r2}
          stroke={color}
          strokeWidth={0.4 * strokeWidth}
          opacity="0.7"
        />,
      );
    }
  }

  let innerGlyph: React.ReactNode = null;
  const gr = rInner - 4;
  switch (innerGlyphKind) {
    case 0:
      innerGlyph = <circle cx={cx} cy={cy} r={gr * 0.55} fill={color} />;
      break;
    case 1:
      innerGlyph = (
        <g transform={`rotate(${innerRot} ${cx} ${cy})`}>
          <line x1={cx - gr} y1={cy} x2={cx + gr} y2={cy} stroke={color} strokeWidth={0.6 * strokeWidth} />
          <line x1={cx} y1={cy - gr} x2={cx} y2={cy + gr} stroke={color} strokeWidth={0.6 * strokeWidth} />
          <circle cx={cx} cy={cy} r={2.5} fill="none" stroke={color} strokeWidth={0.5 * strokeWidth} />
        </g>
      );
      break;
    case 2:
      innerGlyph = (
        <g>
          <ellipse cx={cx} cy={cy} rx={gr * 0.7} ry={gr * 0.95} fill="none" stroke={color} strokeWidth={0.7 * strokeWidth} />
          <line x1={cx - gr * 0.5} y1={cy} x2={cx + gr * 0.5} y2={cy} stroke={color} strokeWidth={0.7 * strokeWidth} />
        </g>
      );
      break;
    case 3:
      innerGlyph = (
        <g transform={`rotate(${innerRot} ${cx} ${cy})`}>
          <polygon
            points={`${cx},${cy - gr} ${cx + gr * 0.866},${cy + gr * 0.5} ${cx - gr * 0.866},${cy + gr * 0.5}`}
            fill="none"
            stroke={color}
            strokeWidth={0.7 * strokeWidth}
          />
        </g>
      );
      break;
    case 4:
      innerGlyph = (
        <g transform={`rotate(${innerRot} ${cx} ${cy})`}>
          <polygon
            points={`${cx},${cy - gr} ${cx + gr},${cy} ${cx},${cy + gr} ${cx - gr},${cy}`}
            fill="none"
            stroke={color}
            strokeWidth={0.6 * strokeWidth}
          />
          <circle cx={cx} cy={cy} r={1.5} fill={color} />
        </g>
      );
      break;
    case 5:
      innerGlyph = (
        <g transform={`rotate(${innerRot} ${cx} ${cy})`}>
          <rect x={cx - gr * 0.8} y={cy - 1} width={gr * 1.6} height={0.6} fill={color} />
          <rect x={cx - gr * 0.5} y={cy - 4} width={gr} height={0.6} fill={color} />
          <rect x={cx - gr * 0.5} y={cy + 3.4} width={gr} height={0.6} fill={color} />
        </g>
      );
      break;
  }

  const crackPath = (() => {
    if (!cracked) return null;
    const a1 = ((crackStart - 90) * Math.PI) / 180;
    const a2 = ((crackStart + 180 - 90) * Math.PI) / 180;
    const r = rOuter - 1;
    const sx = cx + Math.cos(a1) * r;
    const sy = cy + Math.sin(a1) * r;
    const ex = cx + Math.cos(a2) * r;
    const ey = cy + Math.sin(a2) * r;
    const segs = 7;
    const pts = [`M ${sx} ${sy}`];
    for (let i = 1; i < segs; i++) {
      const t = i / segs;
      const lx = sx + (ex - sx) * t;
      const ly = sy + (ey - sy) * t;
      const jitter = crackJitter[i - 1] ?? 0;
      const dx = ex - sx;
      const dy = ey - sy;
      const len = Math.hypot(dx, dy);
      const px = -dy / len;
      const py = dx / len;
      pts.push(`L ${(lx + px * jitter).toFixed(2)} ${(ly + py * jitter).toFixed(2)}`);
    }
    pts.push(`L ${ex} ${ey}`);
    return (
      <g>
        <path d={pts.join(" ")} stroke="var(--oxblood)" strokeWidth={1.2 * strokeWidth} fill="none" strokeLinejoin="miter" />
        <path d={pts.join(" ")} stroke="var(--bg)" strokeWidth={0.4 * strokeWidth} fill="none" />
      </g>
    );
  })();

  const isActive = (n: 1 | 2 | 3 | 4) => layer >= n;
  const layerProps = (n: 1 | 2 | 3 | 4, baseOpacity: number) => ({
    className: `sigil-layer sigil-layer--${n} ${isActive(n) ? "sigil-layer--active" : ""}`,
    style: { "--layer-opacity": String(baseOpacity) } as React.CSSProperties,
  });

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      role="img"
      aria-label={title || `Sigil ${seed}`}
      style={glow ? { filter: "drop-shadow(0 0 8px var(--bronze-glow))" } : undefined}
      className={`${sweeping ? "sigil--sweeping" : ""} ${flash ? "sigil__sweep-flash" : ""}`}
    >
      {/* Layer 1 — Identity: outer ring, cardinal ticks, dotted ring */}
      <g {...layerProps(1, cracked ? 0.4 : 1)}>
        <circle
          cx={cx}
          cy={cy}
          r={rOuter}
          fill="none"
          stroke={color}
          strokeWidth={0.8 * strokeWidth}
        />
        <g className="sigil__sweep sigil-layer__ticks">{ticks}</g>
        <g className="sigil-layer__dots" opacity={cracked ? 0.75 : 1}>{outerDots}</g>
      </g>
      {/* Layer 2 — Permissions: mid ring, wedges, mid ticks */}
      <g {...layerProps(2, 1)}>
        <circle
          cx={cx}
          cy={cy}
          r={rMid}
          fill="none"
          stroke={color}
          strokeWidth={0.5 * strokeWidth}
          opacity={cracked ? 0.3 : 0.8}
        />
        <g className="sigil-layer__wedges" opacity={cracked ? 0.2 : 1}>{wedges}</g>
        <g className="sigil-layer__midticks" opacity={cracked ? 0.3 : 1}>{midTicks}</g>
      </g>
      {/* Layer 3 — Audit: inner ring + inner square */}
      <g {...layerProps(3, 1)}>
        <circle
          cx={cx}
          cy={cy}
          r={rInner}
          fill="none"
          stroke={color}
          strokeWidth={0.5 * strokeWidth}
          opacity={cracked ? 0.3 : 0.9}
        />
        {hasInnerSquare && (
          <rect
            className="sigil-layer__square"
            x={cx - rInner * 0.6}
            y={cy - rInner * 0.6}
            width={rInner * 1.2}
            height={rInner * 1.2}
            fill="none"
            stroke={color}
            strokeWidth={0.4 * strokeWidth}
            opacity={cracked ? 0.3 : 0.6}
            transform={`rotate(45 ${cx} ${cy})`}
          />
        )}
      </g>
      {/* Layer 4 — Soul: central glyph (stamps in with scale spring) */}
      <g {...layerProps(4, cracked ? 0.4 : 1)} className={`sigil-layer sigil-layer--glyph ${isActive(4) ? "sigil-layer--active" : ""}`}>
        {innerGlyph}
      </g>
      {crackPath}
    </svg>
  );
}

export type StatusType = "active" | "paused" | "revoked" | "archived";

export function StatusRing({ status = "active", size = 14 }: { status?: StatusType; size?: number }) {
  const cx = 8;
  const cy = 8;
  const r = 6;
  if (status === "revoked" || status === "archived") {
    return (
      <svg width={size} height={size} viewBox="0 0 16 16" aria-label={status}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--oxblood)" strokeWidth="1" opacity="0.3" strokeDasharray="2 2" />
        <circle cx={cx} cy={cy} r={1.2} fill="var(--oxblood)" />
      </svg>
    );
  }
  if (status === "paused") {
    return (
      <svg width={size} height={size} viewBox="0 0 16 16" aria-label="paused">
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--slate)" strokeWidth="1" opacity="0.4" />
        <path d={`M ${cx} ${cy - r} A ${r} ${r} 0 0 1 ${cx} ${cy + r}`} fill="none" stroke="var(--bone)" strokeWidth="1.4" />
      </svg>
    );
  }
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" aria-label="active">
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--bone)" strokeWidth="1.2" />
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--bone)" strokeWidth="1.2" opacity="0.4">
        <animate attributeName="r" from="6" to="9" dur="2.4s" repeatCount="indefinite" />
        <animate attributeName="opacity" from="0.4" to="0" dur="2.4s" repeatCount="indefinite" />
      </circle>
      <circle cx={cx} cy={cy} r="1.6" fill="var(--bone)" />
    </svg>
  );
}

export function Hallmark({ size = 12, tone = "bronze" }: { size?: number; tone?: "bronze" | "oxblood" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 12 12" aria-label="committed on-chain">
      <polygon
        points="6,1 10.2,3.5 10.2,8.5 6,11 1.8,8.5 1.8,3.5"
        fill="none"
        stroke={`var(--${tone})`}
        strokeWidth="0.8"
      />
      <path
        d="M 3.6 6 L 5.2 7.6 L 8.4 4.4"
        fill="none"
        stroke={`var(--${tone})`}
        strokeWidth="1"
        strokeLinecap="square"
        strokeLinejoin="miter"
      />
    </svg>
  );
}

export function HallmarkBadge({ children, tone = "bronze" }: { children: React.ReactNode; tone?: "bronze" | "oxblood" }) {
  return (
    <span
      className="hallmark-stamp"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "var(--s-2)",
        font: "var(--type-kicker)",
        letterSpacing: "var(--tracking-kicker)",
        textTransform: "uppercase",
        color: `var(--${tone})`,
      }}
    >
      <Hallmark tone={tone} />
      {children}
    </span>
  );
}
